import type { RefObject } from 'react'
import React, { useState } from 'react'
import { IconButton, Stack } from '@mui/material'
import { Fullscreen, Monitor, Person, Splitscreen, VolumeOff, VolumeUp } from '@mui/icons-material'

interface VideoComponentProps {
  videoRef: RefObject<HTMLVideoElement>
  hidden: boolean
}

/** Displays webcam feed */
const ProctorCombinedVideoComponent = ({ videoRef, hidden }: VideoComponentProps): JSX.Element => {
  const videoContainerRef = React.createRef()
  const playerRef = React.createRef()
  let fullScreenMode = false
  const views = { camera: 'camera', screen: 'screen', combined: 'combined' }
  let currentView = views.combined

  const [isAudioMute, setAudiomute] = useState<boolean>(false)

  const updateStyles = (view: string) => {
    if (videoContainerRef === null || videoRef === null || playerRef === null) {
      return
    }
    currentView = view
    switch (view) {
      case 'camera':
        if (!fullScreenMode) {
          playerRef.current.classList.add('view-zoom')
          videoRef.current.classList.remove('view-fullscreen')
          videoContainerRef.current.classList.remove('view-fullscreen')
          videoContainerRef.current.classList.add('view-zoom')
        } else {
          videoRef.current.classList.remove('view-fullscreen-combined')
          videoRef.current.classList.add('view-fullscreen')
          videoContainerRef.current.classList.remove('view-zoom')
          videoContainerRef.current.classList.add('view-fullscreen')
        }
        videoContainerRef.current.scrollTop = 0
        break
      case 'screen':
        if (!fullScreenMode) {
          playerRef.current.classList.add('view-zoom')
          videoRef.current.classList.remove('view-fullscreen')
          videoContainerRef.current.classList.remove('view-fullscreen')
          videoContainerRef.current.classList.add('view-zoom')
          videoContainerRef.current.scrollTop = 720
        } else {
          videoRef.current.classList.remove('view-fullscreen-combined')
          videoRef.current.classList.add('view-fullscreen')
          videoContainerRef.current.classList.remove('view-zoom')
          videoContainerRef.current.classList.add('view-fullscreen')
          videoContainerRef.current.scrollTop = 1080
        }
        break
      case 'combined':
        if (!fullScreenMode) {
          playerRef.current.classList.remove('view-zoom')
          videoRef.current.classList.remove('view-fullscreen')
          videoRef.current.classList.remove('view-fullscreen-combined')
          videoContainerRef.current.classList.remove('view-fullscreen')
          videoContainerRef.current.classList.remove('view-zoom')
        } else {
          playerRef.current.classList.remove('view-zoom')
          videoRef.current.classList.remove('view-fullscreen')
          videoRef.current.classList.add('view-fullscreen-combined')
          videoContainerRef.current.classList.remove('view-zoom')
          videoContainerRef.current.classList.add('view-fullscreen')
          videoContainerRef.current.scrollTop = 0
        }
        break
    }
  }

  const changeCameraZoom = () => {
    updateStyles(views.camera)
  }
  const changeScreenZoom = () => {
    updateStyles(views.screen)
  }
  const normalZoom = () => {
    updateStyles(views.combined)
  }

  function enterFS(element) {
    if (element.requestFullscreen) {
      element.requestFullscreen()
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen()
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen()
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen()
    }
  }

  function exitFS() {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    }
  }

  const toggleFullScreen = () => {
    if (!fullScreenMode) {
      fullScreenMode = true
      enterFS(playerRef.current)
    } else {
      fullScreenMode = false
      exitFS()
    }
    updateStyles(currentView)
  }

  const toggleAudioMute = () => {
    videoRef.current.muted = !videoRef.current.muted
    setAudiomute(!isAudioMute)
  }

  const renderNormalZoomButton = () => {
    return (
      <IconButton onClick={normalZoom}>
        <Splitscreen sx={{ fontSize: '2rem', color: 'white' }} />
      </IconButton>
    )
  }
  const renderCameraZoomButton = () => {
    return (
      <IconButton onClick={changeCameraZoom}>
        <Person sx={{ fontSize: '2rem', color: 'white' }} />
      </IconButton>
    )
  }
  const renderScreenZoomButton = () => {
    return (
      <IconButton onClick={changeScreenZoom}>
        <Monitor sx={{ fontSize: '2rem', color: 'white' }} />
      </IconButton>
    )
  }

  const renderFullScreenButton = () => {
    return (
      <IconButton onClick={toggleFullScreen}>
        <Fullscreen sx={{ fontSize: '2rem', color: 'white' }} />
      </IconButton>
    )
  }

  const renderAudioMuteButton = () => {
    return (
      <IconButton onClick={toggleAudioMute}>
        {isAudioMute ? (
          <VolumeOff sx={{ fontSize: '2rem', color: 'white' }} />
        ) : (
          <VolumeUp sx={{ fontSize: '2rem', color: 'white' }} />
        )}
      </IconButton>
    )
  }

  const renderAudioVolumeProp = () => {
    return <div>{renderAudioMuteButton()}</div>
  }

  return (
    <>
      <div className="player" ref={playerRef} style={{ display: hidden ? 'none' : '' }}>
        <div className="videoContainer" ref={videoContainerRef}>
          <div className="player-controls">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={3}>
                {renderAudioVolumeProp()}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={3}>
                {renderNormalZoomButton()} {renderCameraZoomButton()} {renderScreenZoomButton()}
                {renderFullScreenButton()}
              </Stack>
            </Stack>
          </div>
          <video className="proctorCombinedVideo" id="CombinedVideo" ref={videoRef} autoPlay></video>
        </div>
      </div>
    </>
  )
}

export { ProctorCombinedVideoComponent }
